import { Either, Maybe } from 'monet';

/**
 * @description Шлюз ролей сотрудников.
 * @param {Axios} axios HTTP клиент.
 * @constructor
 */
export default function EmployeeRoleGateway(axios) {
  /**
   * @description Создать новую роль.
   * @param {object} data Данные.
   * @returns {Promise<Either<any, any>>}
   */
  this.create = (data) => axios
    .post('/api/employee-roles', data)
    .then(
      async (response) => Either.Right(response.data),
      async (error) => {
        if (error.isAxiosError && (
          error.response.status === 400
          || error.response.status === 403
        )) {
          return Either.left(error.response.data);
        }
        throw error;
      },
    );
  /**
   * @description Изменить существующую роль.
   * @param {number} roleId Идентификатор роли.
   * @returns {function(any): Promise<Either<any, any>>}
   */
  this.edit = (roleId) => (data) => axios
    .put(`/api/employee-roles/${roleId}`, data)
    .then(
      async (response) => Either.Right(response.data),
      async (error) => {
        if (error.isAxiosError) {
          if (error.response && (error.response.status === 400 || error.response.status === 403)) {
            return Either.left(error.response.data);
          }
          if (!error.response) {
            throw new Error(`Network error: сервер по адресу /api/employee-roles/${roleId} не ответил на запрос`);
          }
        }
        throw error;
      },
    );
  /**
   * @description Получить данные для создания роли.
   * @returns {Promise<Maybe<any>>}
   */
  this.getDataForCreation = () => axios
    .get('/api/employee-role-creation')
    .then(async (response) => {
      const { items: groupedPermissions } = response.data;
      return Maybe.Just({ groupedPermissions });
    });
  /**
   * @description Получить актуальные роли.
   *
   * @returns {Promise<Maybe<{canAddRole: boolean, items: any[]}>>}
   */
  this.getList = () => axios.get(
    '/api/employee-roles',
  ).then(async (response) => {
    const canAddRole = response.data.actions.includes('add');
    const items = response.data.items.map((role) => ({
      canBeEdit: role.actions.includes('edit'),
      canBeRemove: role.actions.includes('remove'),
      id: role.id,
      isDefault: role.tags.includes('default'),
      name: role.name,
      permissions: role.permissions,
    }));
    const onBoardingStatus = response.data.data.on_boarding_status;
    return Maybe.Just({ canAddRole, items, onBoardingStatus });
  });
  /**
   * @description Удалить роль.
   * @param {number} roleId Идентификатор роли.
   * @returns {Promise<Either<any,any>>}
   */
  this.remove = (roleId) => axios
    .delete(`/api/employee-roles/${roleId}`)
    .then(
      async (response) => {
        const { title } = response.data;
        return Either.Right({ title });
      },
      async (error) => {
        if (error.isAxiosError && (
          error.response.status === 400
          || error.response.status === 403
          || error.response.status === 404
        )) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );
}
